import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import type { ModuloIscrizione } from '@corsi/modulo.ts';
import { CornicePubblica } from '../components/iscrizione/CornicePubblica';
import { ElencoBiglietti } from '../components/iscrizione/biglietti/ElencoBiglietti';
import { ModuloBiglietto } from '../components/iscrizione/biglietti/ModuloBiglietto';
import { BRANDING } from '../lib/branding';
import {
  contaBiglietti,
  leggiAcquirente,
  leggiBiglietto,
  messaggioErrore,
  nomeBiglietti,
  precompilaDaAcquirente,
  quantiNelModulo,
  tokenValido,
  type BigliettoPubblico,
  type EventoPubblico,
  type PaginaAcquirente,
} from '../lib/eventi/bigliettiPubblici';

/**
 * Registrazione dei biglietti comprati sul sito (slabsrl.com), senza login.
 *
 *   /registrazione-evento?o=<codice>  chi ha comprato: i suoi biglietti, uno per
 *                                     riga; per ognuno compila i dati o copia il
 *                                     link da mandare a chi lo usera'
 *   /registrazione-evento?b=<codice>  il modulo di un biglietto (il link girato)
 *
 * Chi si registra entra nella lista dell'evento e riceve l'invito a scegliere
 * il percorso: lo fa la funzione evento-pubblico (vedi biglietti.ts).
 */

type Stato =
  | { fase: 'caricamento' }
  | { fase: 'errore'; messaggio: string }
  | { fase: 'acquirente'; pagina: PaginaAcquirente; aperto: BigliettoPubblico | null }
  | { fase: 'biglietto'; evento: EventoPubblico; biglietto: BigliettoPubblico }
  | {
      fase: 'fatto';
      evento: EventoPubblico;
      nome: string;
      conConsenso: boolean;
      bigliettoPdf: boolean;
      quantiBiglietti: number;
      tornaAllElenco: boolean;
    };

function Conferma({
  nome,
  evento,
  conConsenso,
  bigliettoPdf,
  quantiBiglietti,
  onElenco,
}: {
  nome: string;
  evento: string;
  conConsenso: boolean;
  bigliettoPdf: boolean;
  quantiBiglietti: number;
  onElenco?: () => void;
}) {
  const arrivaPdf =
    quantiBiglietti > 1
      ? `Entro pochi minuti arrivano per email anche i ${quantiBiglietti} biglietti, uno per email, da stampare o mostrare dal telefono all'ingresso.`
      : "Entro pochi minuti arriva per email anche il biglietto, da stampare o mostrare dal telefono all'ingresso.";
  return (
    <div className="foglio-quadretti mt-8 rounded-2xl px-6 py-10 text-center shadow-2xl ring-1 ring-black/5">
      <CheckCircle2 className="mx-auto h-14 w-14 text-green-600" aria-hidden="true" />
      <h2 className="mt-3 text-2xl font-bold text-gray-900">Registrazione completata</h2>
      <p className="mt-3 text-lg leading-relaxed text-gray-700">
        {nome.trim() ? `Grazie ${nome.trim()}` : 'Grazie'}, la partecipazione a <strong>{evento}</strong> è registrata. A breve
        arriverà un'email con il link per scegliere il percorso.
      </p>
      {bigliettoPdf && (
        <p className="mt-3 text-base leading-relaxed text-gray-600">{arrivaPdf}</p>
      )}
      {conConsenso && (
        <p className="mt-3 text-base leading-relaxed text-gray-600">
          Arriverà anche un'email per confermare che vuoi ricevere le nostre comunicazioni: controlla anche nella posta
          indesiderata.
        </p>
      )}
      {onElenco && (
        <button
          type="button"
          onClick={onElenco}
          className="mt-6 rounded-full border-2 border-primary-600 bg-white px-6 py-2 text-base font-semibold text-primary-700 transition hover:bg-primary-50"
        >
          Torna ai biglietti
        </button>
      )}
    </div>
  );
}

function Riquadro({ children }: { children: ReactNode }) {
  return <div className="glass-morphism-card mt-6 rounded-2xl px-5 py-4 text-lg leading-relaxed text-white">{children}</div>;
}

export default function RegistrazioneEventoPage() {
  const [parametri] = useSearchParams();
  const codiceAcquirente = parametri.get('o');
  const codiceBiglietto = parametri.get('b');
  const [stato, setStato] = useState<Stato>({ fase: 'caricamento' });

  useEffect(() => {
    document.title = `Registrazione biglietti — ${BRANDING.nomeBreve}`;
  }, []);

  const carica = useCallback(async () => {
    setStato({ fase: 'caricamento' });
    if (tokenValido(codiceAcquirente)) {
      const esito = await leggiAcquirente(codiceAcquirente);
      setStato(esito.ok ? { fase: 'acquirente', pagina: esito.dati, aperto: null } : { fase: 'errore', messaggio: messaggioErrore(esito.codice) });
      return;
    }
    if (tokenValido(codiceBiglietto)) {
      const esito = await leggiBiglietto(codiceBiglietto);
      setStato(
        esito.ok
          ? { fase: 'biglietto', evento: esito.dati.evento, biglietto: esito.dati.biglietto }
          : { fase: 'errore', messaggio: messaggioErrore(esito.codice) },
      );
      return;
    }
    setStato({ fase: 'errore', messaggio: messaggioErrore('link_non_valido') });
  }, [codiceAcquirente, codiceBiglietto]);

  useEffect(() => {
    void carica();
  }, [carica]);

  const nomeEvento =
    stato.fase === 'acquirente' ? stato.pagina.evento.nome : stato.fase === 'biglietto' || stato.fase === 'fatto' ? stato.evento.nome : null;

  return (
    <CornicePubblica>
      <h1 className="text-center text-4xl font-bold tracking-wide text-white drop-shadow-lg sm:text-5xl">REGISTRAZIONE</h1>
      <p className="mt-2 text-center text-base uppercase tracking-[0.2em] text-gray-300">{nomeEvento ?? 'Biglietti'}</p>

      {stato.fase === 'caricamento' && (
        <div className="mt-16 flex justify-center text-white">
          <Loader2 className="h-10 w-10 animate-spin" aria-label="Caricamento" />
        </div>
      )}

      {stato.fase === 'errore' && <Riquadro>{stato.messaggio}</Riquadro>}

      {stato.fase === 'fatto' && (
        <Conferma
          nome={stato.nome}
          evento={stato.evento.nome}
          conConsenso={stato.conConsenso}
          bigliettoPdf={stato.bigliettoPdf}
          quantiBiglietti={stato.quantiBiglietti}
          onElenco={stato.tornaAllElenco ? () => void carica() : undefined}
        />
      )}

      {stato.fase === 'acquirente' && <VistaAcquirente stato={stato} setStato={setStato} />}

      {stato.fase === 'biglietto' && <VistaBiglietto stato={stato} setStato={setStato} />}
    </CornicePubblica>
  );
}

function VistaAcquirente({
  stato,
  setStato,
}: {
  stato: Extract<Stato, { fase: 'acquirente' }>;
  setStato: (s: Stato) => void;
}) {
  const { pagina, aperto } = stato;
  const conti = contaBiglietti(pagina.biglietti);
  const datiAcquirente: Partial<ModuloIscrizione> = precompilaDaAcquirente(pagina.acquirente);

  if (aperto?.token) {
    return (
      <>
        <button
          type="button"
          onClick={() => setStato({ ...stato, aperto: null })}
          className="mt-6 inline-flex items-center gap-2 text-base font-medium text-white hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Torna ai biglietti
        </button>
        <ModuloBiglietto
          token={aperto.token}
          evento={pagina.evento}
          biglietto={aperto}
          datiAcquirente={datiAcquirente}
          onFatto={(esito) => setStato({ fase: 'fatto', evento: pagina.evento, ...esito, tornaAllElenco: true })}
        />
      </>
    );
  }

  return (
    <>
      <Riquadro>
        {conti.daRegistrare === 0 ? (
          <>Tutti i biglietti sono registrati. Grazie! Ogni partecipante riceve per email il link per scegliere il percorso.</>
        ) : (
          <>
            {pagina.acquirente.nome ? `Ciao ${pagina.acquirente.nome}, ` : ''}
            {conti.validi === 1 ? 'hai un biglietto' : `hai ${conti.validi} biglietti`} per{' '}
            <strong className="text-primary-300">{pagina.evento.nome}</strong>. Per ognuno ci servono i dati di chi lo userà:
            compilali qui, oppure copia il link e mandalo alla persona, così li inserisce lei.
          </>
        )}
      </Riquadro>
      <ElencoBiglietti biglietti={pagina.biglietti} onCompila={(b) => setStato({ ...stato, aperto: b })} />
    </>
  );
}

function VistaBiglietto({
  stato,
  setStato,
}: {
  stato: Extract<Stato, { fase: 'biglietto' }>;
  setStato: (s: Stato) => void;
}) {
  const { evento, biglietto } = stato;
  if (biglietto.stato !== 'da_registrare' || !biglietto.token) {
    const codice = biglietto.stato === 'annullato' ? 'biglietto_annullato' : 'gia_registrato';
    return <Riquadro>{messaggioErrore(codice, evento.email_riferimento)}</Riquadro>;
  }
  const coppia = quantiNelModulo(biglietto) > 1;
  return (
    <>
      <Riquadro>
        Hai un biglietto per <strong className="text-primary-300">{evento.nome}</strong>
        {coppia && <> ({nomeBiglietti(biglietto)})</>}.{' '}
        {coppia
          ? "Compila i tuoi dati una volta sola: valgono per tutti e due i biglietti. Subito dopo riceverai l'email per scegliere il tuo percorso."
          : "Compila i tuoi dati: subito dopo riceverai l'email per scegliere il tuo percorso."}
      </Riquadro>
      <ModuloBiglietto
        token={biglietto.token}
        evento={evento}
        biglietto={biglietto}
        onFatto={(esito) => setStato({ fase: 'fatto', evento, ...esito, tornaAllElenco: false })}
      />
    </>
  );
}
